import "./PollySurveyAuthoringFormSettings.scss";

import { Flex, Segment, Text } from "@fluentui/react-northstar";
import { FieldDescriptors } from "@shopify/react-form-state";
import { getSurveyTypeDisplay } from "@teams/cards";
import { TeamsAnonymityLevel } from "@teams/types";
import { TeamInfoDTO } from "@teams/types/api";
import moment from "moment";
import React, { useState } from "react";

import { Dropdown } from "../../../../_shared/components/Dropdown";
import {
  CheckboxField,
  DropdownField,
  TextInputField,
} from "../../../../_shared/components/fields";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { notEmpty } from "../../../../_shared/utils";
import { rruleToHumanizedString } from "../../../../_shared/utils/recurringHelpers";
import { DeliveryMethodField } from "../DeliveryMethodField";
import { OpenDurationField } from "../OpenDurationField";
import { ResultsVisibilityField } from "../ResultsVisibilityField";
import { SchedulePollyDialog } from "../SchedulePollyDialog";
import { SettingLabel } from "../SettingLabel";
import { AuthoringFields, PollySurveyAuthoringProps } from "./types";

type Props = {
  fields: FieldDescriptors<AuthoringFields>;
  isLimitedEdit?: boolean;
  teamsDetails: TeamInfoDTO | undefined;
  customRecipient?: boolean;
} & Pick<
  PollySurveyAuthoringProps,
  | "parentSurvey"
  | "referenceTemplateId"
  | "editScheduleId"
  | "allowManualSend"
  | "allowScheduledSend"
  | "compressed"
  | "isMobile"
  | "isEdit"
>;

export const PollySurveyAuthoringFormSettings: React.FC<Props> = props => {
  const {
    referenceTemplateId,
    allowManualSend,
    allowScheduledSend,
    editScheduleId,
    compressed,
    isMobile,
    parentSurvey,
    fields,
    isLimitedEdit,
    isEdit,
    teamsDetails,
    customRecipient,
  } = props;

  const [showScheduleSelector, setShowScheduleSelector] = useState<boolean>();

  return (
    <>
      <Flex column>
        {!isLimitedEdit && !customRecipient && (
          <DeliveryMethodField
            field={fields.deliveryMethod}
            label={`Deliver ${getSurveyTypeDisplay(fields.type.value)}`}
            fluid
            teamsDetails={teamsDetails}
            isMobile={isMobile}
            compressed={compressed}
          />
        )}

        <ResultsVisibilityField
          field={fields.resultsVisibility}
          realTime={fields.type.value !== "quiz"}
          privateResults
          afterClose
          compressed={compressed}
          isMoble={isMobile}
          disabled={isLimitedEdit && parentSurvey?.resultsVisibility === "private"}
        />

        {fields.type.value !== "quiz" && (
          <Flex vAlign="center" wrap>
            <SettingLabel
              icon="anonomous-icon"
              content={compressed ? "Anonymity" : "Response from audience"}
            />
            <DropdownField<TeamsAnonymityLevel>
              field={fields.anonymity}
              fluid
              className="polly-authoring__dropdown"
              isMobile={isMobile}
              items={[
                {
                  label: "anonymous",
                  value: "anonymous",
                },
                {
                  label: "non-anonymous",
                  value: "none",
                },
              ]}
              disabled={isLimitedEdit && parentSurvey?.anonymity === "anonymous"}
            />
          </Flex>
        )}

        {!isLimitedEdit && (
          <Flex vAlign="center" wrap>
            <SettingLabel icon="clock-icon" content="Send" />
            <Dropdown
              {...(isMobile && { design: { paddingLeft: "5px;" }, inline: true })}
              fluid
              className="polly-authoring__dropdown"
              isItemSelected={item => {
                if (item.value === "sendnow") {
                  return !!fields.sendAt.value && !fields.rrule.value;
                }

                if (item.value === "sendmanually") {
                  return !fields.sendAt.value && !fields.rrule.value;
                }

                if (item.value === "on date") {
                  return fields.rrule.value.length > 0;
                }

                return false;
              }}
              items={[
                editScheduleId
                  ? undefined
                  : {
                      key: "send-now",
                      header: "now",
                      value: "sendnow",
                      onClick: () => {
                        fields.sendAt.onChange(new Date());
                        fields.rrule.onChange("");
                      },
                    },
                editScheduleId || !allowManualSend
                  ? undefined
                  : {
                      key: "send-manually",
                      header: "manual",
                      value: "sendmanually",
                      onClick: () => {
                        fields.sendAt.onChange(undefined);
                        fields.rrule.onChange("");
                      },
                    },
                !allowScheduledSend
                  ? undefined
                  : {
                      key: "schedule",
                      header: "schedule",
                      value: "on date",
                      onClick: () => {
                        setShowScheduleSelector(true);
                      },
                    },
              ].filter(notEmpty)}
              value={
                fields.rrule.value
                  ? `${rruleToHumanizedString(fields.rrule.value)} at ${moment(
                      fields.sendAt.value
                    ).format("h:mm a")}`
                  : moment(fields.sendAt.value).isAfter()
                  ? `on ${moment(fields.sendAt.value).format("lll")}`
                  : fields.sendAt.value
                  ? "now"
                  : "manual"
              }
            />
          </Flex>
        )}

        <Flex vAlign="center" wrap>
          <SettingLabel
            icon="clock-icon"
            content={fields.openDuration.value ? "Close after" : "Close"}
          />
          <OpenDurationField
            field={fields.openDuration}
            fluid
            allowManual={!fields.rrule.value}
            isMobile={isMobile}
          />
        </Flex>

        {fields.type.value !== "quiz" && referenceTemplateId && fields.commentsEnabled.value && (
          <Flex gap="gap.small" column wrap>
            <Flex vAlign="center">
              <SettingLabel icon="open-text-icon" content="Comments enabled" />
              <DropdownField<boolean>
                field={fields.commentsEnabled}
                fluid
                className="polly-authoring__dropdown"
                isMobile={isMobile}
                items={[
                  {
                    label: "yes",
                    value: true,
                  },
                  {
                    label: "no",
                    value: false,
                  },
                ]}
              />
            </Flex>

            {fields.commentsEnabled.value && (
              <div style={{ marginLeft: "1.5rem" }}>
                <TextInputField
                  fluid
                  label="Comment message"
                  field={fields.commentsAppeal}
                  placeholder="Please elaborate on your choice"
                />
              </div>
            )}
          </Flex>
        )}
      </Flex>

      {isLimitedEdit && (
        <Segment>
          <Flex column gap="gap.small">
            <Flex
              gap="gap.smaller"
              vAlign="center"
              style={{ paddingLeft: "4px", marginBottom: "5px" }}
            >
              <PollyIcon inheritColor icon="notify" size="large" />
              <Text content="Revision options for updating this polly" weight="bold" />
            </Flex>
            <Flex gap="gap.smaller" vAlign="center">
              <CheckboxField
                trueLabel={
                  <>
                    <Text content="Remove existing votes: "></Text>
                    <Text
                      content="Start fresh with edit update to the polly. A notification will be sent by default in this case"
                      weight="bold"
                    ></Text>
                  </>
                }
                falseLabel={
                  <>
                    <Text content="Remove existing votes: "></Text>
                    <Text content="Start fresh with edit update to the polly" weight="bold"></Text>
                  </>
                }
                field={fields.removeExistingVotesOnEdit}
                onChange={(_e, d) => {
                  if (d?.checked) {
                    fields.notifyUsersOnEdit.onChange(true);
                  }
                  fields.removeExistingVotesOnEdit.onChange(d?.checked ?? false);
                }}
              />
            </Flex>
            <Flex gap="gap.smaller" vAlign="center">
              <CheckboxField
                trueLabel={
                  <>
                    <Text content="Notify audience: "></Text>
                    <Text
                      content="Let people know that they polly has changed"
                      weight="bold"
                    ></Text>
                  </>
                }
                falseLabel={
                  <>
                    <Text content="Notify audience: "></Text>
                    <Text
                      content="Let people know that they polly has changed"
                      weight="bold"
                    ></Text>
                  </>
                }
                field={fields.notifyUsersOnEdit}
                defaultChecked={fields.notifyUsersOnEdit.value}
                disabled={fields.removeExistingVotesOnEdit.value}
              ></CheckboxField>
            </Flex>
          </Flex>
        </Segment>
      )}

      {showScheduleSelector && (
        <SchedulePollyDialog
          open={showScheduleSelector}
          onCancel={() => setShowScheduleSelector(false)}
          onSave={(sendAt, rule) => {
            fields.sendAt.onChange(sendAt);
            if (rule) {
              fields.rrule.onChange(rule);
              if (!fields.openDuration.value) {
                fields.openDuration.value = "P1D";
              }
            }
            setShowScheduleSelector(false);
          }}
          rrule={fields.rrule.value}
          sendAt={fields.sendAt.value || new Date()}
          recuringRequired={!!editScheduleId}
          hideRecurring={!editScheduleId && isEdit}
        />
      )}
    </>
  );
};
